import * as Signals from 'signals';
import * as ko from 'knockout';
import * as moment from 'moment';
import * as _ from 'underscore';
import * as localisationHelper from 'localisationHelper';

// Models
import { JoiningStep, JoiningStepEnum } from 'Content/ts/Models/Join/JoiningStep';
import { MandateDetails } from 'Content/ts/Models/Join/MandateDetails';
import { MarketingPreferences } from 'Content/ts/Models/Join/MarketingPreferences';
import { MembershipType } from 'Content/ts/Models/Join/MembershipType';
import { IMembershipPriceModel } from 'Content/ts/Models/Join/Interfaces/IMembershipPrice';
import { PriceBreakdownDetailsModel } from 'Content/ts/Models/Shared/PriceBreakdown/PriceBreakdownDetailsModel';
import { IAgreementRestrictionDetail } from 'Content/ts/Models/Join/Interfaces/IAgreementRestriction';
import { IMembershipPriceDetailsRequestModel } from 'Content/ts/Models/Join/Interfaces/IMembershipPriceDetailsRequestModel';
import { DateTimeConfiguration } from 'Content/ts/Models/Shared/DateTime/dateTimePickerConfiguration';
import { PeriodType } from 'Content/ts/Models/Join/PeriodType';
import { DateTimeWorkflowStates } from 'Content/ts/Shared/Controls/DateTime/Services/workflowStates';
import { ContactDetails } from 'Content/ts/Models/Join/ContactDetails';
import { DateConfiguration } from 'Content/ts/Models/Shared/DateTime/datePickerConfiguration';
import { PaymentProviderTypes } from 'Content/ts/Enums/UniversalBasket/paymentProviderTypes';
import { IJourneyContactField, ContactFieldEntityType } from 'Content/ts/Models/Join/Interfaces/IJourneyContactField';
import { BillingDetails } from 'Content/ts/Models/Contact/Details/billingDetails';
import { DirectDebitConfirmationConfiguration } from 'Content/ts/Models/Shared/DirectDebitDetails/DirectDebitConfirmationConfiguration';

// Services
import { ConfigurableTextService } from "Scripts/ts/Localisation/ConfigurableTextService";
import { DateTimeFormatterService } from 'Content/ts/Shared/Controls/DateTime/Services/dateTimeFormatterService';
import { JoinService } from 'Content/ts/Join/Services/JoinService';
import { ContactFieldService } from 'Content/ts/Join/Services/contactFieldService';

export const Deps = {
    LocalisationHelper: localisationHelper
}
export class JoinConfiguration {
    journeyId: number;
    contactId: number;
    facilitySelected: Signals.Signal;
    agreementSelected: Signals.Signal;
    collectionDateChanged: Signals.Signal;
    directDebitInitialise: Signals.Signal;

    directDebitConfirmationConfiguration: DirectDebitConfirmationConfiguration;

    steps: JoiningStep[];
    currentStep: KnockoutObservable<number>;
    currentStepModel: KnockoutComputed<JoiningStep>;

    customMessages: {
        GymSelectionStepHeader: string;
        AboutYouStepHeader: string;
        PaymentStepHeader: string;
        ClubLabel: string;
        ClubPanelHeaderText: string;
        ClubHelpText: string;
        LoadingClubs: string;
        ClubRequiredValidationMessage: string;
    };

    membershipPriceRequest: KnockoutObservable<IMembershipPriceDetailsRequestModel>;

    facilityId: KnockoutObservable<number>;
    facilityMainLocationId: KnockoutObservable<number>;
    facilityName: KnockoutObservable<string>;
    membershipStartDate: KnockoutObservable<moment.Moment>;
    membershipType: KnockoutObservable<MembershipType>;
    promoCodeEnabled: KnockoutObservable<boolean>;
    promoDiscount: KnockoutObservable<number>;
    promoCode: KnockoutObservable<string>;
    priceDetails: PriceBreakdownDetailsModel;
    membershipsLoading: KnockoutObservable<boolean>;
    priceDetailsLoading: KnockoutObservable<boolean>;
    facilityLoading: KnockoutObservable<boolean>;
    facilitiesAssigned: KnockoutObservable<boolean>;
    selectedMembershipPrice: KnockoutObservable<IMembershipPriceModel>;
    selectedMembershipPriceId: KnockoutObservable<number>;
    billingDetails: BillingDetails;
    contactDetails: ContactDetails;
    mandateDetails: MandateDetails;
    marketingPreferences: MarketingPreferences;
    genderRestriction: KnockoutObservable<IAgreementRestrictionDetail>;
    ageRestriction: KnockoutObservable<IAgreementRestrictionDetail>;
    basketCost: KnockoutComputed<number>;
    contactFieldsLoaded: KnockoutObservable<boolean>;
    paymentProvider: KnockoutObservable<PaymentProviderTypes>;

    facilityEarliestStartDate: KnockoutObservable<moment.Moment>;
    enableFlexibleStartDate: KnockoutObservable<boolean>;
    maxPeriodForFlexibleStartDate: KnockoutObservable<number>;
    maxPeriodForFlexibleStartDateType: KnockoutObservable<PeriodType>;
    minFlexibleStartDate: KnockoutComputed<moment.Moment>;
    maxFutureFlexibleStartDate: KnockoutComputed<moment.Moment>;
    maxMemberServiceStartDate: KnockoutObservable<moment.Moment>;
    membershipStartDateString: KnockoutComputed<string>;
    startDateConfigLoaded: KnockoutObservable<boolean>;
    flexibleStartDateConfiguration: KnockoutObservable<DateTimeConfiguration>;
    enableReferrer: KnockoutObservable<boolean>;
    contactFieldMapping: any;
    entityMap: any;

    wpmFormId: string;

    wpmPaymentRequestXml: KnockoutObservable<string>;
    wpmPostUrl: KnockoutObservable<string>;

    constructor(journeyId: number, facilityId: number | null, membershipPriceId: number | null, contactId: number) {
        this.journeyId = journeyId;
        this.contactId = contactId;

        this.paymentProvider = ko.observable(null);
        this.contactFieldMapping = {};

        this.currentStep = ko.observable(1);
        this.membershipType = ko.observable();
        this.promoCodeEnabled = ko.observable(null);
        this.promoDiscount = ko.observable(0);
        this.promoCode = ko.observable(null);
        this.facilitySelected = new Signals.Signal();
        this.agreementSelected = new Signals.Signal();
        this.collectionDateChanged = new Signals.Signal();
        this.directDebitInitialise = new Signals.Signal();

        this.directDebitConfirmationConfiguration = {
            requiresConfirmation: false,
            showDirectDebitModalSignal: new Signals.Signal(),
            callback: null,
        }



        this.contactDetails = new ContactDetails();
        this.billingDetails = new BillingDetails();
        this.mandateDetails = new MandateDetails();
        this.marketingPreferences = new MarketingPreferences();

        this.priceDetails = new PriceBreakdownDetailsModel({
            CycleFee: null,
            FirstBillDate: null,
            PriceRequestGroupId: null,
            BillingMethodId: null,
            BillingCycleId: null,
            MCD: null,
            AutomaticDiscount: null,
            Fees: []
        });

        this.priceDetailsLoading = ko.observable(false);
        this.membershipsLoading = ko.observable(false);
        this.facilityLoading = ko.observable(false);
        this.facilitiesAssigned = ko.observable(false);
        this.selectedMembershipPrice = ko.observable<IMembershipPriceModel>(null);
        this.selectedMembershipPriceId = ko.observable(membershipPriceId);
        this.facilityName = ko.observable(null);
        this.facilityEarliestStartDate = ko.observable(null);
        this.membershipStartDate = ko.observable<moment.Moment>(null);
        this.facilityId = ko.observable(facilityId);
        this.facilityMainLocationId = ko.observable(null);
        this.membershipPriceRequest = ko.observable(null);
        this.contactFieldsLoaded = ko.observable(false);

        this.enableFlexibleStartDate = ko.observable(false);
        this.maxPeriodForFlexibleStartDate = ko.observable(0);
        this.maxPeriodForFlexibleStartDateType = ko.observable(PeriodType.Days);
        this.maxMemberServiceStartDate = ko.observable(null);
        this.startDateConfigLoaded = ko.observable(false);
        this.flexibleStartDateConfiguration = ko.observable(null);
        this.enableReferrer = ko.observable(false);
        this.genderRestriction = ko.observable(null);
        this.ageRestriction = ko.observable(null);

        this.wpmPaymentRequestXml = ko.observable(null);
        this.wpmPostUrl = ko.observable(null);

        this.initialiseFieldMapping();
        this.loadAssignedContactFields();
        this.retrieveCustomMessages();

        this.steps = [
            new JoiningStep(JoiningStepEnum.GymSelection, 1, this.customMessages.GymSelectionStepHeader, true),
            new JoiningStep(JoiningStepEnum.PersonalDetails, 2, this.customMessages.AboutYouStepHeader),
            new JoiningStep(JoiningStepEnum.Payment, 3, this.customMessages.PaymentStepHeader)
        ];

        this.wpmFormId = 'wpmPaymentForm';

        this.createComputeds();
        this.createSubscriptions();
    }

    private retrieveCustomMessages(): void {
        
        this.customMessages = {
            ClubLabel: ConfigurableTextService.RetrieveTextSync("DefaultFacilityTitle"),
            ClubPanelHeaderText: ConfigurableTextService.RetrieveTextSync("JourneyClubSelectPanelHeaderText"),
            GymSelectionStepHeader: ConfigurableTextService.RetrieveTextSync("JourneyBreadcrumbStepOneText"),
            AboutYouStepHeader: localisationHelper.getString("JoinResources", "AboutYouStepHeader"),
            PaymentStepHeader: localisationHelper.getString("JoinResources", "PaymentStepHeader"),
            ClubHelpText: ConfigurableTextService.RetrieveTextSync("JourneyClubHelpText"),
            LoadingClubs: ConfigurableTextService.RetrieveTextSync("JourneyLoadingClubsText"),
            ClubRequiredValidationMessage: ConfigurableTextService.RetrieveTextSync("JourneyClubRequiredValidationText")
        };
    }

    private createComputeds(): void {
        this.currentStepModel = ko.pureComputed(() => {
            return this.steps.find(s => s.stepNumber === this.currentStep());
        });

        this.minFlexibleStartDate = ko.pureComputed(() => {

            if (!this.enableFlexibleStartDate()) {
                return null;
            }

            if (this.maxMemberServiceStartDate()) {
                const period = this.maxPeriodForFlexibleStartDate();
                const date = moment(this.maxMemberServiceStartDate()).subtract(period, this.maxPeriodForFlexibleStartDateType() === PeriodType.Days ? 'd' : 'M');
                if (date.isAfter(moment())) {
                    return date;
                }
            }
            return moment().startOf("day");
        });

        this.maxFutureFlexibleStartDate = ko.pureComputed(() => {

            if (!this.enableFlexibleStartDate()) {
                return null;
            }

            if (this.maxMemberServiceStartDate()) {
                return this.maxMemberServiceStartDate();
            }

            return moment().add(this.maxPeriodForFlexibleStartDate(), this.maxPeriodForFlexibleStartDateType() === PeriodType.Days ? 'd' : 'M');
        });

        this.membershipStartDateString = ko.pureComputed(() => {
            return this.membershipStartDate() !== null
                ? this.membershipStartDate().format(DateTimeFormatterService.TimeZoneAgnosticAjaxFormat)
                : null;
        });

        this.basketCost = ko.pureComputed(() => {
            return this.priceDetails.HasFees()
                ? this.priceDetails.TotalPrice() - this.priceDetails.AutomaticDiscount() - this.promoDiscount()
                : 0;
        });
    }

    private createSubscriptions(): void {
        this.currentStep.subscribe((step: number) => {
            this.getStep(step).enabled(true);
        })
    }

    private initialiseFieldMapping(): void {
        this.entityMap = {};
        this.entityMap[ContactFieldEntityType.Contact] = 'contact';
        this.entityMap[ContactFieldEntityType.Address] = 'address';

        this.contactFieldMapping = {};
        _.forEach(this.entityMap, (entityName: string) => {
            this.contactFieldMapping[entityName] = [];    // initialise each entity type
        });
    }

    loadAssignedContactFields(): void{
        JoinService.getAssignedContactFields(this.journeyId)
            .then((result: IJourneyContactField[]) => {
                result.forEach((contactField: IJourneyContactField) => {
                    this.contactFieldMapping[this.entityMap[contactField.EntityTypeId]].push(contactField);
                });

                this.contactFieldsLoaded(true);
            });
    }

    setStartDateConfiguration(): void {
        if (this.enableFlexibleStartDate() && (this.minFlexibleStartDate().isBefore(this.maxFutureFlexibleStartDate()))) {
            const betweenDateMin = this.minFlexibleStartDate().format(DateTimeFormatterService.LocalisedMomentShortDateFormat);
            const betweenDateMax = this.maxFutureFlexibleStartDate().format(DateTimeFormatterService.LocalisedMomentShortDateFormat);

            this.flexibleStartDateConfiguration({
                AllowDateTimeClear: false,
                ComponentState: DateTimeWorkflowStates.DateMode,
                TimeLabel: '',
                DateLabel: Deps.LocalisationHelper.getString("GenericResources", "StartDate"),
                TimeConfiguration: null,
                DateConfiguration: {
                    View: "days",
                    AllowClear: false,
                    IsRequired: true,
                    BetweenDates: [
                        betweenDateMin, betweenDateMax
                    ]
                } as DateConfiguration
            } as DateTimeConfiguration);

            this.startDateConfigLoaded(true);
        }
        else {
            this.enableFlexibleStartDate(false);
            this.flexibleStartDateConfiguration(null);
        }
    }

    getValidationFields(step: number): KnockoutObservable<any>[] {
        return this.steps.find(s => s.stepNumber === step).validationFields;
    }

    getStep = (stepType: JoiningStepEnum): JoiningStep => {
        return this.steps.find(s => s.stepType === stepType);
    };

    fieldVisible(element: HTMLElement): boolean {
        return ContactFieldService.getFieldProperties(element, this.contactFieldMapping).visible;
    }

    fieldRequired(element: HTMLElement): boolean {
        return ContactFieldService.getFieldProperties(element, this.contactFieldMapping).required;
    }

    billingDetailsClear(): void {
        this.billingDetails.clearValues();
    }

    billingDetailsPopulateFromContactDetails(): void {
        if (this.contactDetails.Addresses.length > 0) {
            this.billingDetails.AddressLine1(this.contactDetails.Addresses[0].AddressLine1());
            this.billingDetails.City(this.contactDetails.Addresses[0].City());
            this.billingDetails.PostCode(this.contactDetails.Addresses[0].Postcode());
            this.billingDetails.Region(this.contactDetails.Addresses[0].County());
            this.billingDetails.Country(this.contactDetails.Addresses[0].Country());
            this.billingDetails.PhoneNumber(this.contactDetails.Addresses[0].Phone());
        }
        this.billingDetails.Email(this.contactDetails.EmailAddress());
        this.billingDetails.FirstName(this.contactDetails.FirstName());
        this.billingDetails.LastName(this.contactDetails.LastName());
    }
}